/**
 * Components Table — per-scan Report page.
 *
 * Lists the components (dependencies) found in a scan: name, version,
 * upgrade status (Vulnerable / Outdated / Up-to-date / Unknown),
 * group, license, risk score, and vulnerability count.
 *
 * Renders via the canonical DataTable primitive — TableToolbar (count
 * chip only, no search or filters), TablePagination (functional
 * page-size dropdown), and the `dtStyles.shell` bordered card chrome
 * shared with every other Athena table page.
 *
 * Lifted from the hand-rolled 418-line implementation; the status-pill
 * + StatusIcon helpers + the version-status palette are ported
 * verbatim (the four pill colours encode the public Vulnerable / Outdated
 * / Up-to-date / Unknown scheme that customers read semantically — they
 * deliberately stay as literal hex inside the JSX).
 */
import React, { useMemo, useState, memo } from 'react';
import DataTable from '../../components/ui/DataTable/DataTable';
import TableToolbar from '../../components/ui/DataTable/TableToolbar';
import TablePagination from '../../components/ui/DataTable/TablePagination';
import StatusPill from '../../components/ui/StatusPill/StatusPill';
import dtStyles from '../../components/ui/DataTable/DataTable.module.css';

const DEFAULT_PAGE_SIZE = 10;

// Map a (version, latestVersion, vulnerabilityCount) tuple to a
// StatusPill config — tone resolves to the canonical Athena status
// palette via `--color-status-*` tokens:
//   vulnerable → error    (red)
//   outdated   → warning  (amber)
//   uptodate   → success  (green)
//   unknown    → neutral  (grey)
// Tooltip text matches the pre-migration pill copy verbatim.
const getVersionStatus = (version, latestVersion, vulnerabilities) => {
  const vulnText = vulnerabilities === 1 ? 'vulnerability' : 'vulnerabilities';
  if (vulnerabilities > 0) {
    return {
      tone: 'error',
      label: 'Vulnerable',
      tooltip: `Version ${version || 'unknown'} has ${vulnerabilities} known ${vulnText}.`,
    };
  }
  if (!latestVersion) {
    return {
      tone: 'neutral',
      label: 'Unknown',
      tooltip: 'Latest version information unavailable.',
    };
  }
  if (version === latestVersion) {
    return {
      tone: 'success',
      label: 'Up to date',
      tooltip: `Version ${version} is up to date.`,
    };
  }
  return {
    tone: 'warning',
    label: 'Outdated',
    tooltip: `Version ${version} is outdated. Latest: ${latestVersion}.`,
  };
};

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
      fill="currentColor"
      opacity="0.5"
    />
  </svg>
);

const ComponentsTable = memo(({
  data = [],
  totalComponents = 0,
  currentPage = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  loading = false,
  onPageChange,
  onPageSizeChange,
}) => {
  const [sort, setSort] = useState(null);

  // Sort is client-side over the current page slice — matches the
  // pre-migration behaviour (the server already paginated, so the
  // visible data is the only thing to sort). Cross-page sort would
  // need server support; deferred.
  const sortedData = useMemo(() => {
    if (!sort || !data?.length) return data ?? [];
    const dir = sort.direction === 'desc' ? -1 : 1;
    return [...data].sort((a, b) => {
      const av = a[sort.column];
      const bv = b[sort.column];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') {
        return av.toLowerCase().localeCompare((bv ?? '').toString().toLowerCase()) * dir;
      }
      return ((av ?? 0) - (bv ?? 0)) * dir;
    });
  }, [data, sort]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Component',
        sortable: true,
        width: '28%',
        render: (row) => row.name,
      },
      {
        key: 'version',
        label: 'Version',
        sortable: true,
        width: '12%',
        render: (row) => row.version || 'N/A',
      },
      {
        key: 'status',
        label: 'Status',
        // Status is derived from (version × latestVersion × vulnerabilities)
        // — there's no single column key to sort on, so the header is
        // non-sortable (matches the pre-migration behaviour: the old
        // status header set `cursor: default`).
        width: '12%',
        align: 'center',
        render: (row) => {
          const s = getVersionStatus(row.version, row.latestVersion, row.vulnerabilities);
          return (
            <span title={s.tooltip}>
              <StatusPill tone={s.tone} label={s.label} size="sm" />
            </span>
          );
        },
      },
      {
        key: 'group',
        label: 'Group',
        sortable: true,
        width: '17%',
        render: (row) => row.group || '-',
      },
      {
        key: 'license',
        label: 'License',
        sortable: true,
        width: '13%',
        render: (row) => row.license || '-',
      },
      {
        key: 'riskScore',
        label: 'Risk Score',
        sortable: true,
        width: '10%',
        align: 'center',
        render: (row) => row.riskScore || 0,
      },
      {
        key: 'vulnerabilities',
        label: 'Vulnerabilities',
        sortable: true,
        width: '10%',
        align: 'center',
        render: (row) => (
          <span
            style={{
              color: row.vulnerabilities > 0 ? '#EF4444' : undefined,
              fontWeight: 500,
            }}
          >
            {row.vulnerabilities || 0}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className={`${dtStyles.shell} ${dtStyles.shellPlain}`}>
      <DataTable
        data={sortedData}
        columns={columns}
        rowKey="uuid"
        loading={loading}
        sort={sort}
        onSortChange={(column, direction) =>
          setSort(column ? { column, direction } : null)
        }
        emptyState={{
          icon: <EmptyIcon />,
          title: 'No components found',
          description: 'This analysis does not contain any components',
        }}
        toolbar={
          <TableToolbar
            showFilterButton={false}
            count={{ value: totalComponents, label: 'components' }}
          />
        }
        pagination={
          <TablePagination
            currentPage={currentPage}
            totalItems={totalComponents}
            itemsPerPage={pageSize}
            onPageChange={onPageChange}
            onItemsPerPageChange={onPageSizeChange}
          />
        }
      />
    </div>
  );
});

ComponentsTable.displayName = 'ComponentsTable';

export default ComponentsTable;
